import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { defaultEducationTrack, EducationStep, EducationTrack, educationTrackSchema } from '@/api/education'
import { educationHierarchyQueryOptions, useCreateEducationTrackMutation, useDeleteEducationStepMutation, useDeleteEducationTrackMutation, useUpdateEducationStepMutation, useUpdateEducationTrackMutation } from '@/api/educationQueryOptions'
import { OrganizationLevelType } from '@/api/organizations'
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions'
import CreateDetailAccordion, {
    useCreateDetail,
} from '@/components/detail/CreateDetailRecord'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import { DetailGridToolbar } from '@/components/mui-x/datagrid/DetailGridToolbar'
import ScoregCheckbox from '@/components/mui/ScoregCheckbox'
import SelectOrganizationLevel from '@/components/select/SelectOrganizationType'
import OrganizationEducationAddStepDialog from '@/dialog/OrganizationEducationAddStepDialog'
import { getOrganization } from '@/store/auth/authSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import AddBoxIcon from '@mui/icons-material/AddBox'
import CancelIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Grid } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import {
    DataGridPremium,
    GridActionsCellItem,
    GridColDef,
    GridColumnVisibilityModel,
    GridLogicOperator,
    GridRowId,
    GridRowModes,
    GridRowModesModel
} from '@mui/x-data-grid-premium'
import { t } from 'i18next'
import React from 'react'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/education',
)({
    component: OrganizationComponent,
})

function OrganizationComponent() {
    const params = Route.useParams()
    const [addStepDialogOpen, setAddStepDialogOpen] = React.useState(false)
    const [selectedRow, setSelectedRow] = React.useState<any>()

    const organizationEducationQuery = useSuspenseQuery(
        educationHierarchyQueryOptions(params.organizationUuid),
    )
    const rows = organizationEducationQuery.data

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('organization', params.organizationUuid, [
            'EducationStep',
        ]),
    )
    const permissions = permissionsQuery.data

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'EducationStep' &&
                p.permissions.some((a) => a === 'create'),
        ).length > 0

    const trackCrud = useDetailCrudEvents<EducationTrack>(
        'educationTrack',
    )

    const createEducationTrackMutation =
        useCreateEducationTrackMutation(
            params.organizationUuid,
            trackCrud.onCreated,
            trackCrud.onError,
        )

    const updateEducationTrackMutation =
        useUpdateEducationTrackMutation(
            params.organizationUuid,
            trackCrud.onUpdated,
            trackCrud.onError,
        )

    const deleteEducationTrackMutation =
        useDeleteEducationTrackMutation(
            params.organizationUuid,
            trackCrud.onDeleted,
            trackCrud.onError,
        )

    const stepCrud = useDetailCrudEvents<EducationStep>(
        'educationStep',
    )

    // const createEducationStepMutation = useCreateEducationStepMutation(
    //   params.organizationUuid,
    //   stepCrud.onCreated,
    //   stepCrud.onError,
    // )
    const updateEducationStepMutation = useUpdateEducationStepMutation(
        params.organizationUuid,
        stepCrud.onUpdated,
        stepCrud.onError,
    )
    const deleteEducationStepMutation = useDeleteEducationStepMutation(
        params.organizationUuid,
        stepCrud.onDeleted,
        stepCrud.onError,
    )

    const [dialogOpen, setDialogOpen] = React.useState(false)

    const { medium, large, xlarge } = useResponsive()

    const handleEditClick = (id: GridRowId) => () => {
        console.log('handleEditClick')
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
    }

    const handleDeleteClick = (id: GridRowId, row: any) => () => {
        console.log('handleDeleteClick on ' + id)
        setSelectedRow(row)
        setDialogOpen(true)
    }

    const handleSaveClick = (id: GridRowId) => () => {
        console.log('handleSaveClick')

        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
    }

    const handleCancelClick = (id: GridRowId) => () => {
        console.log('handleCancelClick')
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        })
    }

    const handleAddStepClick = (_id: GridRowId, row: any) => () => {
        setAddStepDialogOpen(true)
        setSelectedRow(row)
    }

    const handleCancel = () => {
        setDialogOpen(false)
    }

    const handleDelete = () => {
        console.log('handleDelete ' + selectedRow.uuid)
        setDialogOpen(false)
        if (selectedRow.educationTrack) {
            deleteEducationStepMutation.mutate(selectedRow)
        } else {
            deleteEducationTrackMutation.mutate(selectedRow)
        }
    }

    const processRowUpdate = (row: any) => {
        if (row.educationTrack) {
            updateEducationStepMutation.mutate(row)
        } else {
            updateEducationTrackMutation.mutate(row)
        }

        return row
    }

    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
        {},
    )

    const columns: GridColDef[] = []
    columns.push({
        field: 'code',
        minWidth: 50,
        headerName: t('property.general.code'),
        editable: true,
    })
    columns.push({
        field: 'name',
        minWidth: 100,
        headerName: t('property.general.name'),
        editable: true,
    })
    columns.push({
        field: 'organizationLevel',
        type: 'singleSelect',
        valueOptions: [
            { value: OrganizationLevelType.TROOP, label: t('enum.organizationLevel.TROOP') },
            { value: OrganizationLevelType.DIVISION, label: t('enum.organizationLevel.DIVISION') },
            { value: OrganizationLevelType.FEDERATION, label: t('enum.organizationLevel.FEDERATION') },
        ],
        valueFormatter: (_params, row) => {
            if (row.organizationLevel) {
                return t(`enum.organizationLevel.${row.organizationLevel}`)
            }
            return ''
        },
        minWidth: 100,
        headerName: t('property.educationTrack.organizationLevel'),
        editable: true,
    })
    columns.push({
        field: 'manual',
        type: 'boolean',
        minWidth: 50,
        headerName: t('property.educationTrack.manual'),
        editable: true,
    })
    columns.push({
        field: 'description',
        minWidth: 100,
        headerName: t('property.general.description'),
        editable: true,
    })
    if (large || xlarge) {
        columns.push({
            field: 'startDate',
            width: 125,
            type: 'date',
            headerName: t('property.general.startDate'),
            valueGetter: (value) => {
                return value && new Date(value)
            },
            editable: true,
        })
    }

    if (medium || large || xlarge) {
        columns.push({
            field: 'detailActions',
            type: 'actions',
            headerName: t('entity.educationStep'),
            width: 50,
            cellClassName: 'actions',

            getActions: ({ id, row }: { id: GridRowId; row: any }) => {
                if (row.educationTrack) {
                    return []
                } else {
                    return [
                        <GridActionsCellItem
                            icon={<AddBoxIcon />}
                            label="Add"
                            key={`add-${id}`}
                            onClick={handleAddStepClick(id, row)}
                        />,
                    ]
                }
            },
        })
        columns.push({
            field: 'actions',
            type: 'actions',
            headerName: t('column.actions'),
            width: 100,
            cellClassName: 'actions',

            getActions: ({
                id,
                row,
            }: {
                id: GridRowId
                row: EducationStep | EducationTrack
            }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit
                const writeable = row.permissions
                    ? row.permissions.some((perm) => perm === 'write')
                    : false
                const deleteable = row.permissions
                    ? row.permissions.some((perm) => perm === 'delete')
                    : false

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            key={`save-${id}`}
                            style={{
                                color: 'primary.main',
                            }}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            key={`cancel-${id}`}
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ]
                }

                const actions = []
                actions.push(
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        key={`edit-${id}`}
                        disabled={!writeable}
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                )

                actions.push(
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        key={`delete-${id}`}
                        disabled={!deleteable}
                        className="textPrimary"
                        onClick={handleDeleteClick(id, row)}
                        color="inherit"
                    />,
                )

                return actions
            },
        })
    }

    const initialVisibilityModel = {
        description: false,
        startDate: false,
        completeDate: true,
        approveMember: true,
        approver: true,
        approveOrganization: true,
        comment: false,
        trackCompleteDate: false,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('organization.education', initialVisibilityModel || {});
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const groupingColDef = {
        headerName: t('property.educationTrack.groupName'),
        minWidth: 100,

        valueGetter: (value: string, row: any) => {
            if (row.EducationTrack && row.educationStep) {
                return row.educationStep.code + ' - ' + row.educationStep.name
            } else if (row.EducationTrack) {
                return row.description
            } else {
                return (
                    row.code +
                    ' - ' +
                    row.name +
                    (row.description ? ' [' + row.description + ']' : '')
                )
            }
            return value
        },
    }

    const onSubmitTrack = (data: EducationTrack) => {
        createEducationTrackMutation.mutate(data)
        console.log(data)
    }

    const createEducationTrack = useCreateDetail<EducationTrack>({
        onSubmit: onSubmitTrack,
        resolver: yupResolver<EducationTrack, any, any>(educationTrackSchema),
        defaultValues: {
            ...defaultEducationTrack,
            organization: {
                uuid: params.organizationUuid,
            },
        },
        title: t('action.new', { entity: t('entity.educationTrack') }),
    })

    const currentOrganization = useSelector(getOrganization)

    const CustomToolbar = () => {
        return (
            <DetailGridToolbar scope='organizationEducation' getScopeName={() => { return currentOrganization.name || currentOrganization.abbreviation || '_' }} />
        )
    }

    if (
        organizationEducationQuery.isLoading ||
        organizationEducationQuery.isFetching
    ) {
        return <LoadProgress />
    }

    return (
        <>
            <Dialog
                open={dialogOpen}
                onClose={handleCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{'Eintrag löschen?'}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t('message.deleteAreYouSure')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Abbrechen</Button>
                    <Button
                        onClick={handleDelete}
                        autoFocus
                        variant="outlined"
                        color="error"
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
            {createable && (
                <CreateDetailAccordion {...createEducationTrack}>
                    <Grid container spacing={2}>
                        <input
                            type="hidden"
                            defaultValue={params.organizationUuid}
                            {...createEducationTrack.form.register('organization.uuid')}
                        />
                        <input
                            type="hidden"
                            defaultValue={defaultEducationTrack.organizationLevel}
                            {...createEducationTrack.form.register('organizationLevel')}
                        />
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={defaultEducationTrack.name}
                                required
                                {...createEducationTrack.form.register('name')}
                                label={t('property.general.name')}
                                slotProps={{ htmlInput: { maxLength: 100 } }}
                                helperText={createEducationTrack.form.formState.errors.name?.message}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 4,
                                lg: 2
                            }}>
                            <TextField
                                defaultValue={defaultEducationTrack.code}
                                required
                                {...createEducationTrack.form.register('code')}
                                label={t('property.general.code')}
                                slotProps={{ htmlInput: { maxLength: 10 } }}
                                helperText={createEducationTrack.form.formState.errors.code?.message}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectOrganizationLevel control={createEducationTrack.form.control} name="organizationLevel" limitDown={true} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregCheckbox
                                name="manual"
                                defaultValue={defaultEducationTrack.manual}
                                control={createEducationTrack.form.control}
                                label={t('property.educationTrack.manual')} />
                        </Grid>
                    </Grid>
                </CreateDetailAccordion>
            )}
            <OrganizationEducationAddStepDialog open={addStepDialogOpen} setOpen={setAddStepDialogOpen} organizationUuid={params.organizationUuid} row={selectedRow} />
            <DataGridPremium
                treeData
                getTreeDataPath={(row) =>
                    row.educationTrack
                        ? [row.educationTrack.uuid, row.uuid]
                        : [row.uuid]
                }
                groupingColDef={groupingColDef}
                rows={rows}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                columns={columns}
                columnVisibilityModel={visibilityModel}
                onColumnVisibilityModelChange={handleVisibilityModelChange}
                slots={{ toolbar: CustomToolbar }}
                slotProps={{
                    filterPanel: {
                        logicOperators: [GridLogicOperator.And],
                        sx: { maxWidth: '100vw' }
                    },
                }}
                defaultGroupingExpansionDepth={2}
                processRowUpdate={(updatedRow) => processRowUpdate(updatedRow)}
                onProcessRowUpdateError={(error) => console.log(error)}
                editMode="row"
                rowModesModel={rowModesModel}
                disableMultipleRowSelection={true}
                disableAggregation={true}
                density="compact"
                showToolbar />
        </>
    );
}


